import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  Edit3,
  Layers,
  PlayCircle,
} from "lucide-react";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import TextReportDialog from "@/components/TextReportDialog";
import { HistoryChip, SearchBox } from "@/components/masters/UI";
import { api, formatApiError } from "@/lib/api";
import { downloadCsv, toCsv } from "@/lib/csv";
import { formatPublishedBy } from "@/lib/audit";

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const fmtPublished = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  return dt.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AllocationsPage = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [fetchingReportId, setFetchingReportId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get("/allocations")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((row) =>
      `${row.date || ""} ${row.shift_name || ""} ${row.name || ""} ${row.status || ""}`
        .toLowerCase()
        .includes(s)
    );
  }, [q, rows]);

  const openOne = (sid) => navigate(`/allocation/${sid}`);

  const handleCopyText = async (row) => {
    const canCopy = row.can_copy_text !== undefined
      ? Boolean(row.can_copy_text)
      : (row.status === "confirmed" && (row.total_rows ?? 0) > 0 && (row.confirmed_rows ?? 0) === (row.total_rows ?? 0));
    if (!canCopy) {
      toast.error("Publish the allocation first");
      return;
    }
    setFetchingReportId(row.id);
    try {
      const { data } = await api.get(`/allocations/${row.id}/text-report`);
      setReportText(data.text || "");
      setReportOpen(true);
    } catch (e) {
      if (e?.response?.status === 409) {
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, can_copy_text: false } : r))
        );
      }
      toast.error(formatApiError(e));
    } finally {
      setFetchingReportId(null);
    }
  };

  const exportList = () => {
    const csv = toCsv(filtered, [
      { header: "Date", get: (r) => r.date || "" },
      { header: "Shift", get: (r) => r.shift_name || "" },
      { header: "Shift Start", get: (r) => r.shift_start || "" },
      { header: "Shift End", get: (r) => r.shift_end || "" },
      { header: "Total Rows", get: (r) => r.total_rows ?? 0 },
      { header: "Confirmed Rows", get: (r) => r.confirmed_rows ?? 0 },
      {
        header: "Status",
        get: (r) => (r.status === "confirmed" ? "Confirmed" : "Draft"),
      },
      { header: "Published At", get: (r) => r.published_at || "" },
    ]);
    downloadCsv(`allocations-history-${new Date().toISOString().slice(0, 10)}`, csv);
    toast.success(`Exported ${filtered.length} allocations`);
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="allocations-page">
      <AppHeader />

      <PageShell
        eyebrow="Department Allocation"
        title="Allocation history"
        subtitle={`Every Allocation for your Location · ${rows.length} total`}
        primaryAction={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportList}
              data-testid="allocations-export"
              className="btn-pill border bg-white px-4 py-2 text-sm font-semibold text-vision-ink shadow-card hover:bg-[color:var(--vision-ice)]"
              style={{ borderColor: "rgba(52,73,94,0.15)" }}
            >
              Export
            </button>
            <button
              type="button"
              onClick={() => navigate("/allocation")}
              className="btn-pill bg-[color:var(--vision-orange)] px-5 py-2.5 text-sm font-semibold text-white shadow-card hover:brightness-105"
              data-testid="allocations-open-today"
            >
              <PlayCircle className="mr-1 inline h-4 w-4" />
              Today&apos;s allocation
            </button>
          </div>
        }
        testId="allocations-shell"
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <SearchBox
            value={q}
            onChange={setQ}
            placeholder="Search by date, shift or status…"
            testId="allocations-search"
          />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-vision-slate/60">
            Showing {filtered.length}
          </span>
        </div>

        {loading ? (
          <div
            className="rounded-2xl border bg-white p-10 text-center text-sm text-vision-slate/70 shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="allocations-loading"
          >
            Loading allocations…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="rounded-2xl border bg-white p-10 text-center text-sm text-vision-slate/70 shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="allocations-empty"
          >
            {rows.length === 0
              ? "No allocations yet for your Location. Close a session's Scan-In to draft one."
              : "No allocations match this search."}
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-2xl border bg-white shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="allocations-list"
          >
            <div
              className="hidden grid-cols-[1.3fr_1.6fr_1fr_1fr_1.2fr_220px] gap-4 border-b bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid"
              style={{ borderColor: "rgba(52,73,94,0.08)" }}
            >
              <div>Date</div>
              <div>Shift</div>
              <div>Rows</div>
              <div>Status</div>
              <div>Published</div>
              <div className="text-right">Actions</div>
            </div>

            {filtered.map((row) => (
              <AllocationRow
                key={row.id}
                row={row}
                onOpen={() => openOne(row.session_id)}
                onCopyText={handleCopyText}
                fetchingReportId={fetchingReportId}
              />
            ))}
          </div>
        )}
        <TextReportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          text={reportText}
        />
      </PageShell>
    </div>
  );
};

const AllocationRow = ({ row, onOpen, onCopyText, fetchingReportId }) => {
  const confirmed = row.status === "confirmed";
  const canCopy = row.can_copy_text !== undefined
    ? Boolean(row.can_copy_text)
    : (confirmed && (row.total_rows ?? 0) > 0 && (row.confirmed_rows ?? 0) === (row.total_rows ?? 0));
  const total = row.total_rows ?? 0;
  const confirmedRows = row.confirmed_rows ?? 0;
  const drafts = Math.max(0, total - confirmedRows);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      data-testid={`allocations-row-${row.id}`}
      className="grid w-full cursor-pointer grid-cols-1 items-center gap-3 border-t px-5 py-4 text-left transition-colors hover:bg-[color:var(--vision-peach)]/25 md:grid-cols-[1.3fr_1.6fr_1fr_1fr_1.2fr_220px] md:gap-4"
      style={{ borderColor: "rgba(52,73,94,0.06)" }}
    >
      <div>
        <div className="font-display text-sm font-bold text-vision-ink">
          {fmtDate(row.date)}
        </div>
        <div className="text-xs text-vision-slate/60 numeric-tabular">
          {row.date}
        </div>
      </div>

      <div>
        <div className="truncate font-display text-sm font-bold text-vision-ink">
          {row.shift_name}
        </div>
        <div className="text-xs text-vision-slate/60">
          <Clock className="mr-1 inline h-3 w-3" />
          {row.shift_start}–{row.shift_end}
        </div>
      </div>

      <div>
        <div className="font-display text-sm font-bold text-vision-ink numeric-tabular">
          {total}
        </div>
        <div className="text-[11px] text-vision-slate/60">
          <Layers className="mr-1 inline h-3 w-3" />
          {confirmedRows}C · {drafts}D
        </div>
      </div>

      <div>
        <HistoryChip
          tone={confirmed ? "success" : "navy"}
          icon={confirmed ? CheckCircle2 : Edit3}
          label={confirmed ? "Confirmed" : "Draft"}
          testId={`allocations-row-${row.id}-status`}
        />
      </div>

      <div>
        {row.published_at ? (
          <>
            <HistoryChip
              tone="slate"
              icon={CalendarDays}
              label={fmtPublished(row.published_at)}
              testId={`allocations-row-${row.id}-published`}
            />
            {formatPublishedBy(row) && (
              <div
                className="mt-1 text-[11px] font-medium text-vision-slate/75"
                data-testid={`allocations-row-${row.id}-published-by`}
              >
                {formatPublishedBy(row)}
              </div>
            )}
          </>
        ) : (
          <span
            className="text-[11px] text-vision-slate/60"
            data-testid={`allocations-row-${row.id}-unpublished`}
          >
            Not published yet
          </span>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <span
          title={canCopy ? undefined : "Publish the allocation first"}
          className="inline-block"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            data-testid={`allocations-row-${row.id}-copy-text`}
            disabled={!canCopy || fetchingReportId === row.id}
            title={canCopy ? "Copy as text" : "Publish the allocation first"}
            onClick={(e) => {
              e.stopPropagation();
              onCopyText(row);
            }}
            className={
              canCopy
                ? "btn-pill border border-[color:var(--vision-navy)] px-3 py-1.5 text-xs font-semibold text-vision-navy transition hover:bg-[color:var(--vision-navy)] hover:text-white disabled:opacity-60"
                : "btn-pill border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-400 opacity-60 cursor-not-allowed"
            }
          >
            <Copy className="mr-1 inline h-3.5 w-3.5" />
            {fetchingReportId === row.id ? "Loading…" : "Copy as text"}
          </button>
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="chip bg-[color:var(--vision-navy)] text-white hover:brightness-110"
        >
          Open <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default AllocationsPage;
